import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Button, 
  Container, 
  Paper, 
  CircularProgress, 
  Tabs, 
  Tab, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions, 
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
   Add as AddIcon,
   Event as EventIcon,
   People as PeopleIcon,
   Photo as PhotoIcon,
   Place as PlaceIcon,
   Edit as EditIcon,
   Delete as DeleteIcon,
   CalendarToday as CalendarIcon,
   Repeat as RepeatIcon,
   Route as RouteIcon,
   PhotoCamera as PhotoCameraIcon,
   Videocam as VideocamIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useEvents } from '../../contexts/EventContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Event, EventType, EventFormData } from '../../types/Event';
import { format, formatDistance } from 'date-fns';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import AddEventDialog from '../../components/EventForms/AddEventDialog';
import EventInvitationHistory from '../../components/invitations/EventInvitationHistory';
import TripEventModal, { TripEventModalData } from '../../components/modals/TripEventModal';
import {
   acceptEventInvitation,
   declineEventInvitation,
   resendEventInvitation,
} from '../../services/eventInvitationService';
import SubscriptionGuard from '../../components/SubscriptionGuard';
import CollapsibleInfoPanel from '../../components/common/CollapsibleInfoPanel';
import {
   useResponsive,
   useResponsiveTypography,
   useResponsiveSpacing,
} from '../../utils/responsive';
import { getDefaultBackgroundStyle } from '../../utils/defaultImages';

// Event type to display name mapping
const eventTypeNames: Record<EventType, string> = {
   social: 'Social Gathering',
   family: 'Family Event',
   cultural: 'Cultural Event',
   sports: 'Sports Event',
   business: 'Business Event',
   other: 'Other Event',
};

// Event type to color mapping
const eventTypeColors: Record<EventType, string> = {
   social: '#FF5722',
   family: '#4CAF50',
   cultural: '#9C27B0',
   sports: '#2196F3',
   business: '#607D8B',
   other: '#795548',
};

interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`event-tabpanel-${index}`}
         aria-labelledby={`event-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
   );
}

function a11yProps(index: number) {
   return {
      id: `event-tab-${index}`,
      'aria-controls': `event-tabpanel-${index}`,
   };
}

const EventsPage: React.FC = () => {
   const { currentUser } = useUser();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();

   const {
      events,
      loading,
      error: eventsError,
      refreshEvents: loadEvents,
      createEvent,
      deleteEvent,
   } = useEvents();
   const [error, setError] = useState<string | null>(null);
   const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
   const [deleteError, setDeleteError] = useState<string | null>(null);
   const [eventDialogOpen, setEventDialogOpen] = useState(false);
   const navigate = useNavigate();
   const [tabValue, setTabValue] = useState(0);
   const {
      checkSubscriptionStatus,
      setSubscriptionModalOpen,
      currentSubscription,
      hasSubscriptions,
   } = useSubscription();

   // Helper function to check if user has created any events
   const hasCreatedAnyEvents = async (): Promise<boolean> => {
      if (!currentUser?.uid) return false;
      try {
         // Query all events and filter by members array where user is owner
         const eventsQuery = query(collection(db, 'events'));
         const eventsSnapshot = await getDocs(eventsQuery);

         // Filter events where user is owner in members array
         const userOwnedEvents = eventsSnapshot.docs.filter((doc) => {
            const eventData = doc.data();
            const members = eventData.members || [];
            const userMember = members.find((m: any) => m.userId === currentUser.uid);
            return userMember && userMember.role === 'owner';
         });

         return userOwnedEvents.length > 0;
      } catch (error) {
         console.error('Error checking events creation status:', error);
         return false;
      }
   };

   // Helper function to check if user can create event
   const canCreateEvent = async (): Promise<boolean> => {
      const hasCreatedEvents = await hasCreatedAnyEvents();

      // Check for paid subscription only (not invited access)
      const hasPaidSubscription =
         hasSubscriptions &&
         currentSubscription &&
         (currentSubscription.status === 'active' ||
            currentSubscription.status === 'trialing' ||
            currentSubscription.status === 'past_due');

      // If user hasn't created any events yet, always allow
      if (!hasCreatedEvents) {
         return true;
      }

      // If user has created events, only allow if they have PAID subscription
      return hasPaidSubscription;
   };

   // Filter logic: userCreated vs shared instead of using tabs
   const [userCreatedEvents, setUserCreatedEvents] = useState<Event[]>([]);
   const [sharedEvents, setSharedEvents] = useState<Event[]>([]);

   // Modal state
   const [modalData, setModalData] = useState<TripEventModalData | null>(null);
   const [modalOpen, setModalOpen] = useState(false);

   // Pro tip dismissible (persisted)
   const [proTipDismissed, setProTipDismissed] = useState(() => {
      try {
         return localStorage.getItem('events_pro_tip_dismissed') === 'true';
      } catch {
         return false;
      }
   });
   const handleDismissProTip = () => {
      try {
         localStorage.setItem('events_pro_tip_dismissed', 'true');
      } catch {}
      setProTipDismissed(true);
   };

   // Handle opening event modal
   const handleEventClick = (event: any) => {
      const modalData: TripEventModalData = {
         id: event.id,
         type: 'event',
         title: event.title,
         description: event.description,
         startDate: event.startDate ? new Date(event.startDate) : undefined,
         endDate: event.endDate ? new Date(event.endDate) : undefined,
         location: event.location?.name || event.location,
         coverImageUrl: event.coverImageUrl || event.coverPhotoUrl,
         createdByName: event.createdByName,
         participantCount: event.participantCount,
         photoCount: event.photoCount,
      };
      setModalData(modalData);
      setModalOpen(true);
   };

   useEffect(() => {
      loadEvents();
   }, [currentUser]);

   // Update when events change to separate created from shared
   useEffect(() => {
      // User created events are in userEvents
      setUserCreatedEvents(events.filter((event) => event.isOwner));

      // Shared events are in all events but not created by the user
      const sharedEvents = events.filter((event) => event.isShared);
      setSharedEvents(sharedEvents);
   }, [events, currentUser]);

   const handleCreateEvent = () => {
      setEventDialogOpen(true);
   };

   const handleEventSubmit = async (eventData: EventFormData) => {
      try {
         let coverImageUrl = null;

         // Upload cover image if provided
         if (eventData.coverImage) {
            const storage = getStorage();
            const storageRef = ref(
               storage,
               `events/covers/${Date.now()}_${eventData.coverImage.name}`
            );
            const uploadTask = await uploadBytesResumable(storageRef, eventData.coverImage);
            coverImageUrl = await getDownloadURL(uploadTask.ref);
         }

         // Create the event with processed data
         const eventToCreate: any = {
            ...eventData,
            isDraft: false,
         };

         // Add cover image URL if we uploaded one
         if (coverImageUrl) {
            eventToCreate.coverImageUrl = coverImageUrl;
         }

         // Add recurrence data if applicable
         if (eventData.recurrence) {
            eventToCreate.isRecurring = true;
            eventToCreate.recurrencePattern = eventData.recurrence.pattern;
            eventToCreate.recurrenceInterval = eventData.recurrence.interval;
            eventToCreate.recurrenceEndType = eventData.recurrence.endType;
            eventToCreate.recurrenceEndOccurrences = eventData.recurrence.endOccurrences;
            eventToCreate.recurrenceEndDate = eventData.recurrence.endDate;
         }

         const eventId = await createEvent(eventToCreate);

         // Refresh events list
         loadEvents();

         return Promise.resolve();
      } catch (error) {
         console.error('Error creating event:', error);
         return Promise.reject(error);
      }
   };

   const handleViewEvent = (eventId: string) => {
      navigate(`/events/${eventId}`);
   };

   const handleEditEvent = (eventId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      navigate(`/events/${eventId}/edit`);
   };

   const handleDeleteEvent = async (eventId: string, event: React.MouseEvent) => {
      event.stopPropagation();

      if (
         window.confirm('Are you sure you want to delete this event? This action cannot be undone.')
      ) {
         try {
            setDeleteLoading(eventId);
            setDeleteError(null);
            await deleteEvent(eventId);
         } catch (err) {
            console.error('Error deleting event:', err);
            setDeleteError('Failed to delete event. Please try again.');
         } finally {
            setDeleteLoading(null);
         }
      }
   };

   const formatEventDate = (date: any) => {
      if (!date) return 'TBD';

      try {
         // Handle Firestore Timestamp
         if (date.toDate) {
            return format(date.toDate(), 'MMM d, yyyy');
         }

         // Handle string date
         if (typeof date === 'string') {
            const parsedDate = new Date(date);
            // Check if the date is valid
            if (!isNaN(parsedDate.getTime())) {
               return format(parsedDate, 'MMM d, yyyy');
            }
            return 'TBD';
         }

         // Handle Date object
         if (date instanceof Date && !isNaN(date.getTime())) {
            return format(date, 'MMM d, yyyy');
         }

         return 'TBD';
      } catch (err) {
         console.error('Error formatting date:', err);
         return 'TBD';
      }
   };

   // Create a safe date formatting helper function
   const formatDateSafe = (dateValue: any, formatStr: string = 'MMM d, yyyy') => {
      try {
         // If it's null or undefined
         if (!dateValue) return 'Unknown';

         // If it's a Firestore timestamp with toDate method
         if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            return format(dateValue.toDate(), formatStr);
         }

         // If it's already a Date object
         if (dateValue instanceof Date) {
            // Verify the date is valid
            if (!isNaN(dateValue.getTime())) {
               return format(dateValue, formatStr);
            }
            return 'Unknown';
         }

         // Try to convert to a Date object if it's a string or number
         const parsedDate = new Date(dateValue);
         if (!isNaN(parsedDate.getTime())) {
            return format(parsedDate, formatStr);
         }

         return 'Unknown';
      } catch (error) {
         console.error('Error formatting date:', error, dateValue);
         return 'Unknown';
      }
   };

   // Upcoming = startDate (or endDate) is in the future (created-by-you only for consistency)
   const isUpcoming = (e: Event): boolean => {
      try {
         const start = e.startDate;
         if (!start) return false;
         let date: Date;
         if (start && typeof (start as any).toDate === 'function') {
            date = (start as any).toDate();
         } else if (typeof start === 'string') {
            date = new Date(start);
         } else if (start instanceof Date) {
            date = start;
         } else {
            date = new Date(start as any);
         }
         return !isNaN(date.getTime()) && date > new Date();
      } catch {
         return false;
      }
   };

   const upcomingCount = userCreatedEvents.filter(isUpcoming).length;
   const recurringCount = userCreatedEvents.filter((e) => e.isRecurring === true).length;

   const renderEventCards = () => {
      // Update the sorting function to handle invalid dates safely
      // Combined events with proper sorting - newest first
      const allEvents = [...userCreatedEvents, ...sharedEvents].sort((a, b) => {
         try {
            // Get timestamp values for dates, with safer handling
            const getValidTimestamp = (dateVal: any): number => {
               // Firestore timestamp
               if (dateVal && dateVal.toDate && typeof dateVal.toDate === 'function') {
                  return dateVal.toDate().getTime();
               }

               // String date
               if (typeof dateVal === 'string') {
                  const parsedDate = new Date(dateVal);
                  return !isNaN(parsedDate.getTime()) ? parsedDate.getTime() : 0;
               }

               // Date object
               if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
                  return dateVal.getTime();
               }

               return 0; // Default timestamp (oldest possible)
            };

            // Sort by start date (newest first)
            const dateA = getValidTimestamp(a.startDate);
            const dateB = getValidTimestamp(b.startDate);
            return dateB - dateA;
         } catch (err) {
            console.error('Error sorting dates:', err);
            return 0;
         }
      });

      if (allEvents.length === 0) {
         return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
               <EventIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.6, mb: 2 }} />
               <Typography variant='h6' gutterBottom>
                  No events found
               </Typography>
               <Typography variant='body1' color='text.secondary' gutterBottom>
                  You don't have any events yet. Create your first event to get started.
               </Typography>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={async () => {
                     if (await canCreateEvent()) {
                        navigate('/events/create');
                     } else {
                        setSubscriptionModalOpen(true);
                     }
                  }}
                  sx={{
                     mt: 2,
                     background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                     boxShadow: '0 4px 15px rgba(1, 148, 154, 0.3)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                        boxShadow: '0 6px 20px rgba(1, 148, 154, 0.4)',
                     },
                  }}
               >
                  Create New Event
               </Button>
            </Box>
         );
      }

      return (
         <Grid container spacing={isMobile ? 2 : 3}>
            {allEvents.map((event) => {
               // if (event?.userId != currentUser?.uid) {
               //    return null;
               // }
               const isUserCreated = event?.userId == currentUser?.uid; //userCreatedEvents.some(e => e.id === event.id);

               // Get user's role from backend data
               const userRole = event.userRole || (event.isOwner ? 'owner' : 'viewer');

               const canEdit = userRole === 'owner' || userRole === 'editor';
               const canDelete = userRole === 'owner';

               return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={event.id}>
                     <Card
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           cursor: 'pointer',
                           transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                           boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                           border: '1px solid',
                           borderColor: 'divider',
                           '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                           },
                           position: 'relative',
                           borderRadius: 2,
                        }}
                        onClick={() => handleEventClick(event)}
                     >
                        {/* Role badge to indicate user's permission level */}
                        <Chip
                           label={
                              userRole === 'owner'
                                 ? 'Owner'
                                 : userRole === 'editor'
                                   ? 'Editor'
                                   : 'Viewer'
                           }
                           size='small'
                           color={
                              userRole === 'owner'
                                 ? 'primary'
                                 : userRole === 'editor'
                                   ? 'secondary'
                                   : 'default'
                           }
                           sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              zIndex: 2,
                              bgcolor:
                                 userRole === 'owner'
                                    ? 'primary.main'
                                    : userRole === 'editor'
                                      ? 'secondary.main'
                                      : 'rgba(0, 0, 0, 0.6)',
                              color: 'white',
                              fontSize: '0.7rem',
                           }}
                        />

                        <Box
                           sx={{
                              height: isMobile ? 120 : 130,
                              position: 'relative',
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                              ...getDefaultBackgroundStyle(
                                 'event',
                                 event.id || '',
                                 event.coverImageUrl
                              ),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              filter:
                                 event.startDate && new Date(event.startDate) < new Date()
                                    ? 'grayscale(70%)'
                                    : 'none',
                           }}
                        >
                           {!event.coverImageUrl && (
                              <>
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 0,
                                       left: 0,
                                       right: 0,
                                       bottom: 0,
                                       background: 'rgba(0, 0, 0, 0.3)',
                                       zIndex: 1,
                                    }}
                                 />
                                 <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
                                    <EventIcon
                                       sx={{ fontSize: 32, color: 'white', mb: 0.5, opacity: 0.9 }}
                                    />
                                 </Box>
                              </>
                           )}
                        </Box>
                        {/* <Box
                           sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                           }}
                        >
                           <Typography variant='caption' sx={{ color: 'white' }}>
                              <CalendarIcon
                                 fontSize='small'
                                 sx={{ verticalAlign: 'middle', mr: 0.5 }}
                              />
                              {formatDateSafe(event.startDate)}
                           </Typography>
                        </Box> */}
                        {event.isRecurring && (
                           <Box
                              sx={{
                                 position: 'absolute',
                                 top: 40, // Positioned below the owner badge
                                 left: 8,
                                 bgcolor: 'rgba(25, 118, 210, 0.8)',
                                 borderRadius: 1,
                                 px: 1,
                                 py: 0.5,
                                 display: 'flex',
                                 alignItems: 'center',
                              }}
                           >
                              <RepeatIcon sx={{ color: 'white', fontSize: '0.7rem', mr: 0.5 }} />
                              <Typography
                                 variant='caption'
                                 sx={{ color: 'white', fontSize: '0.7rem' }}
                              >
                                 Recurring
                              </Typography>
                           </Box>
                        )}
                        <CardContent
                           sx={{
                              flexGrow: 1,
                              py: isMobile ? 1.5 : 1,
                              px: isMobile ? 2 : 1.5,
                           }}
                        >
                           {/* Title and Type - more compact */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                                 mb: isMobile ? 1 : 0.5,
                              }}
                           >
                              <Typography
                                 variant='subtitle1'
                                 component='h3'
                                 noWrap
                                 sx={{
                                    fontWeight: 700,
                                    fontSize: isMobile ? '1rem' : '0.95rem',
                                    lineHeight: isMobile ? 1.3 : 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                 }}
                              >
                                 {event.title}
                              </Typography>
                              <Chip
                                 label={event.eventType}
                                 size='small'
                                 sx={{
                                    bgcolor:
                                       eventTypeColors[event.eventType as EventType] || '#757575',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.6rem',
                                    height: '18px',
                                 }}
                              />
                           </Box>

                           {/* Date - compact format */}
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <CalendarIcon
                                 fontSize='small'
                                 sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.9rem' }}
                              />
                              <Typography
                                 variant='caption'
                                 color='text.secondary'
                                 sx={{ fontSize: '0.7rem' }}
                              >
                                 {formatDateSafe(event.startDate)}
                                 {event.endDate &&
                                    event.startDate !== event.endDate &&
                                    ` - ${formatDateSafe(event.endDate)}`}
                              </Typography>
                           </Box>

                           {/* Location - compact format */}
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <PlaceIcon
                                 fontSize='small'
                                 sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.9rem' }}
                              />
                              <Typography
                                 variant='caption'
                                 color='text.secondary'
                                 noWrap
                                 sx={{
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                 }}
                              >
                                 {typeof event.location === 'object' && event.location?.name
                                    ? event.location.name
                                    : typeof event.location === 'string'
                                      ? event.location
                                      : 'Location TBD'}
                              </Typography>
                           </Box>

                           {/* Description - more compact */}
                           {event.description && (
                              <Typography
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    mb: 0.5,
                                    lineHeight: 1.2,
                                 }}
                              >
                                 {event.description.replace(/<[^>]*>/g, '')}
                              </Typography>
                           )}

                           {/* Compact metadata row */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 mt: 0.5,
                              }}
                           >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                 <PeopleIcon
                                    fontSize='small'
                                    sx={{ color: 'text.secondary', mr: 0.3, fontSize: '0.9rem' }}
                                 />
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ fontSize: '0.65rem' }}
                                 >
                                    {event.participants?.length || 0}
                                 </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                 {event.isVirtual && (
                                    <Chip
                                       label='Virtual'
                                       size='small'
                                       variant='outlined'
                                       sx={{
                                          fontSize: '0.55rem',
                                          height: '16px',
                                          minWidth: 'auto',
                                       }}
                                    />
                                 )}
                                 {event.isPublic && (
                                    <Chip
                                       label='Public'
                                       size='small'
                                       variant='outlined'
                                       sx={{
                                          fontSize: '0.55rem',
                                          height: '16px',
                                          minWidth: 'auto',
                                       }}
                                    />
                                 )}
                                 {event.requireRegistration && (
                                    <Chip
                                       label='RSVP'
                                       size='small'
                                       variant='outlined'
                                       sx={{
                                          fontSize: '0.55rem',
                                          height: '16px',
                                          minWidth: 'auto',
                                       }}
                                    />
                                 )}
                              </Box>
                           </Box>
                        </CardContent>
                        {(canEdit || canDelete) && (
                           <CardActions
                              sx={{ justifyContent: 'flex-end', pt: 0, pb: 0.5, px: 1.5 }}
                           >
                              {canEdit && (
                                 <Tooltip title='Edit Event'>
                                    <IconButton
                                       size='small'
                                       color='primary'
                                       onClick={(e) => event.id && handleEditEvent(event.id, e)}
                                    >
                                       <EditIcon />
                                    </IconButton>
                                 </Tooltip>
                              )}
                              {canDelete && (
                                 <Tooltip title='Delete Event'>
                                    <IconButton
                                       size='small'
                                       color='error'
                                       onClick={(e) => event.id && handleDeleteEvent(event.id, e)}
                                       disabled={deleteLoading === event.id}
                                    >
                                       {deleteLoading === event.id ? (
                                          <CircularProgress size={20} />
                                       ) : (
                                          <DeleteIcon />
                                       )}
                                    </IconButton>
                                 </Tooltip>
                              )}
                           </CardActions>
                        )}
                     </Card>
                  </Grid>
               );
            })}
         </Grid>
      );
   };

   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   const handleAcceptInvitation = async (invitationId: string): Promise<void> => {
      if (!currentUser?.uid) {
         setError('You must be logged in to accept invitations.');
         return;
      }

      try {
         await acceptEventInvitation(invitationId, currentUser.uid);
         // Refresh events after accepting invitation
         loadEvents();
         setError(null);
      } catch (error) {
         console.error('Error accepting invitation:', error);
         setError('Failed to accept invitation. Please try again.');
      }
   };

   const handleDeclineInvitation = async (invitationId: string): Promise<void> => {
      try {
         await declineEventInvitation(invitationId);
         setError(null);
      } catch (error) {
         console.error('Error declining invitation:', error);
         setError('Failed to decline invitation. Please try again.');
      }
   };

   const handleResendInvitation = async (invitationId: string): Promise<void> => {
      try {
         await resendEventInvitation(invitationId);
         setError(null);
      } catch (error) {
         console.error('Error resending invitation:', error);
         setError('Failed to resend invitation. Please try again.');
      }
   };

   return (
      <SubscriptionGuard redirectOnNoSubscription={true}>
         <Container
            maxWidth='lg'
            sx={{
               px: isMobile ? 1 : 2,
               py: isMobile ? 1 : 2,
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  mb: isMobile ? 2 : 3,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 2 : 0,
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(180deg, rgba(1, 148, 154, 0.05) 0%, transparent 5rem)',
               }}
            >
               <Typography
                  variant='h5'
                  component='h1'
                  sx={{
                     fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  My Events
               </Typography>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={async () => {
                     if (await canCreateEvent()) {
                        navigate('/events/create');
                     } else {
                        setSubscriptionModalOpen(true);
                     }
                  }}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                     background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                     boxShadow: '0 4px 15px rgba(1, 148, 154, 0.3)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                        boxShadow: '0 6px 20px rgba(1, 148, 154, 0.4)',
                     },
                     minHeight: isMobile ? '48px' : '40px',
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? fontSize.body1 : 'inherit',
                     width: isMobile ? '100%' : 'auto',
                     maxWidth: isMobile ? '200px' : 'none',
                  }}
               >
                  New Event
               </Button>
            </Box>

            <Box sx={{ width: '100%', mt: 3 }}>
               <Box>
                  {/* <Box sx={{ mb: 4 }}>
                  <Grid container spacing={3}>
                     <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 2, boxShadow: 2 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <CalendarIcon sx={{ color: 'primary.main', mr: 1 }} />
                              <Typography variant='h6'>Smart Scheduling</Typography>
                           </Box>
                           <Typography variant='body2' color='text.secondary'>
                              Our AI assistant helps you find the perfect date and time for your
                              event by analyzing participants' availability and preferences.
                           </Typography>
                        </Paper>
                     </Grid>
                     <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 2, boxShadow: 2 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <PlaceIcon sx={{ color: 'secondary.main', mr: 1 }} />
                              <Typography variant='h6'>Activity Suggestions</Typography>
                           </Box>
                           <Typography variant='body2' color='text.secondary'>
                              Get personalized recommendations for activities, restaurants, and
                              attractions based on your event location and participants' interests.
                           </Typography>
                        </Paper>
                     </Grid>
                     <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3, height: '100%', borderRadius: 2, boxShadow: 2 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <PhotoIcon sx={{ color: 'success.main', mr: 1 }} />
                              <Typography variant='h6'>Photo Collaboration</Typography>
                           </Box>
                           <Typography variant='body2' color='text.secondary'>
                              Create shared photo albums for your events, allowing all participants
                              to contribute their photos and create lasting memories together.
                           </Typography>
                        </Paper>
                     </Grid>
                  </Grid>

                  <Divider sx={{ mb: 3, mt: 3 }}>
                     <Chip label='Event Planning with AI' color='primary' />
                  </Divider>
               </Box> */}

                  {/* Event summary cards with improved styling */}
                  <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
                     <Grid item xs={6} sm={6} md={3}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              textAlign: 'center',
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                           }}
                        >
                           <CalendarIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.9 }} />
                           <Typography
                              variant='h3'
                              gutterBottom
                              sx={{
                                 fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                              }}
                           >
                              {userCreatedEvents.length}
                           </Typography>
                           <Typography
                              variant='subtitle1'
                              sx={{
                                 fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                              }}
                           >
                              Created by you
                           </Typography>
                        </Paper>
                     </Grid>
                     <Grid item xs={6} sm={6} md={3}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              textAlign: 'center',
                              bgcolor: 'secondary.light',
                              color: 'secondary.contrastText',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                           }}
                        >
                           <PeopleIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.9 }} />
                           <Typography
                              variant='h3'
                              gutterBottom
                              sx={{
                                 fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                              }}
                           >
                              {sharedEvents.length}
                           </Typography>
                           <Typography
                              variant='subtitle1'
                              sx={{
                                 fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                              }}
                           >
                              Shared with you
                           </Typography>
                        </Paper>
                     </Grid>
                     <Grid item xs={6} sm={6} md={3}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              textAlign: 'center',
                              bgcolor: 'success.light',
                              color: 'success.contrastText',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                           }}
                        >
                           <EventIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.9 }} />
                           <Typography
                              variant='h3'
                              gutterBottom
                              sx={{
                                 fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                              }}
                           >
                              {upcomingCount}
                           </Typography>
                           <Typography
                              variant='subtitle1'
                              sx={{
                                 fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                              }}
                           >
                              Upcoming events
                           </Typography>
                        </Paper>
                     </Grid>
                     <Grid item xs={6} sm={6} md={3}>
                        <Paper
                           sx={{
                              p: isMobile ? 2 : 3,
                              textAlign: 'center',
                              bgcolor: 'info.light',
                              color: 'info.contrastText',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                           }}
                        >
                           <RepeatIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.9 }} />
                           <Typography
                              variant='h3'
                              gutterBottom
                              sx={{
                                 fontSize: isMobile ? fontSize.h4 : fontSize.h2,
                              }}
                           >
                              {recurringCount}
                           </Typography>
                           <Typography
                              variant='subtitle1'
                              sx={{
                                 fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                              }}
                           >
                              Recurring events
                           </Typography>
                        </Paper>
                     </Grid>
                  </Grid>

                  {/* Event cards section with Pro Tip */}
                  {(userCreatedEvents.length > 0 || sharedEvents.length > 0) && (
                     <>
                        {!proTipDismissed && (
                           <Alert
                              severity='info'
                              onClose={handleDismissProTip}
                              icon={false}
                              sx={{ mb: 2 }}
                           >
                              <strong>Pro tip:</strong> Click an event card to view details, manage
                              invitations, and collaborate. Use AI tools to enhance planning.
                           </Alert>
                        )}

                        {renderEventCards()}
                     </>
                  )}
               </Box>
            </Box>

            {/* Event Creation Dialog */}
            <AddEventDialog
               open={eventDialogOpen}
               onClose={() => setEventDialogOpen(false)}
               onSubmit={handleEventSubmit}
            />

            {/* Trip/Event Modal */}
            <TripEventModal
               open={modalOpen}
               onClose={() => {
                  setModalOpen(false);
                  setModalData(null);
               }}
               data={modalData}
            />
         </Container>
      </SubscriptionGuard>
   );
};

export default EventsPage; 